import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageWrapper } from '../../components/layout';
import { TaskBoard, TaskGroups, TaskPeek, TaskViews } from '../../components/tasks-ledger';
import { TaskFormDialog } from '../../components/tasks';
import { LedgerSheet } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import {
  useBoardColumns, useCompleteTask, useListGroups, useReopenTask, useTaskDetail, useUpdateTaskInline,
} from '../../hooks/useTaskQueries';
import { useLedgerListKeys } from '../../hooks/useLedgerListKeys';
import { useAuth } from '../../hooks/useAuth';
import { Task, TaskStatus, TaskViewKey } from '../../types/task.types';
import { cn } from '../../lib/cn';

type Mode = 'list' | 'board';

export const TasksPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [view, setView] = useState<TaskViewKey>('mine');
  const [mode, setMode] = useState<Mode>('list');
  const [search, setSearch] = useState('');
  // ?selected=<taskId> is the deep-link seam from notification emails,
  // mirroring the register's /risks?selected= contract.
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('selected'));
  const [peekOpen, setPeekOpen] = useState(searchParams.has('selected'));
  const [dialogOpen, setDialogOpen] = useState(searchParams.get('create') === '1');
  // Captured object, not an id-into-current-rows derivation: a refetch that
  // drops the row mid-edit must not flip the dialog to create mode.
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const toast = useToast();

  // The palette's "c t" navigates to /tasks?create=1 — also mid-session.
  useEffect(() => {
    if (searchParams.get('create') === '1') setDialogOpen(true);
  }, [searchParams]);

  // Strip the seeded ?selected once — back/refresh must not re-open the peek.
  useEffect(() => {
    if (!searchParams.has('selected')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('selected');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // List groups stay enabled in board mode: they are the mobile fallback (the
  // board is desktop-only) and they feed the masthead count.
  const listGroups = useListGroups(view, search, true);
  const boardColumns = useBoardColumns(view, search, mode === 'board');
  // The awaiting-approval view is status-shaped, so it can't ride the column
  // params like mine/overdue do — the board honors it by showing only its column.
  const visibleColumns =
    view === 'awaiting-approval'
      ? boardColumns.filter((column) => column.key === 'awaiting-approval')
      : boardColumns;
  const surface = mode === 'board' ? visibleColumns : listGroups;

  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const update = useUpdateTaskInline();
  const busy = complete.isPending || reopen.isPending || update.isPending;

  const rows = useMemo(() => surface.flatMap((group) => group.rows), [surface]);
  const selectedTask = rows.find((task) => task.id === selectedId) ?? null;
  // A selected task can leave the loaded groups (view/mode switch, or a status
  // edit made from the peek itself); fetch it by id so the peek survives.
  const peekFallback = useTaskDetail(peekOpen && selectedTask == null ? selectedId : null);
  const peekTask = selectedTask ?? peekFallback.data ?? null;
  const loadFailed = surface.some((group) => group.isError);
  const total = listGroups.reduce((sum, group) => sum + group.count, 0);
  const countsPending = listGroups.every((group) => group.isPending);

  useEffect(() => {
    if (loadFailed) toast.error('Failed to load tasks');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFailed]);

  // Tailwind's lg breakpoint; the peek is a right rail on desktop and a bottom
  // sheet on mobile, and the sheet portals to <body>, so CSS can't gate it.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const index = rows.findIndex((task) => task.id === selectedId);
      const from = index < 0 ? -delta : index;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedId],
  );
  useLedgerListKeys({
    enabled: !dialogOpen,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  const saveError = () => toast.error('Save failed');
  const toggleComplete = (task: Task) => {
    if (task.status === 'COMPLETED') reopen.mutate(task.id, { onError: saveError });
    else complete.mutate(task.id, { onError: saveError });
  };
  const claim = (task: Task) => {
    if (!user) return;
    update.mutate({ id: task.id, data: { assignee: user.id } }, { onError: saveError });
  };
  const dropStatus = (task: Task, status: TaskStatus) => {
    update.mutate({ id: task.id, data: { status } }, { onError: saveError });
  };
  const openRisk = (riskId: string) => navigate(`/risks?selected=${riskId}`);

  const selectView = (next: TaskViewKey) => setView(next);
  const resetView = () => {
    setView('all');
    setSearch('');
  };

  const openCreate = () => {
    setEditingTask(null);
    setDialogOpen(true);
  };
  const openEdit = (task: Task) => {
    setEditingTask(task);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
    if (searchParams.has('create')) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  };
  // Cache invalidation lives in useCreateTask/useUpdateTask (they sweep the
  // whole task query family), so success here is presentation only.
  const handleFormSuccess = () => {
    toast.success(editingTask ? 'Task updated' : 'Task recorded');
  };

  const modeClass = (on: boolean) =>
    cn('cap-label cursor-pointer', on && '!text-accent border-b border-accent');

  return (
    <PageWrapper
      title="Tasks"
      subtitle={countsPending ? undefined : `${total} task${total === 1 ? '' : 's'} on file`}
      noPaper
      maxWidth="xl"
      actions={
        <span className="flex items-baseline gap-3">
          <span className="hidden items-baseline gap-1.5 md:flex">
            <button type="button" aria-pressed={mode === 'list'} onClick={() => setMode('list')} className={modeClass(mode === 'list')}>
              List
            </button>
            <span aria-hidden className="cap-label">·</span>
            <button type="button" aria-pressed={mode === 'board'} onClick={() => setMode('board')} className={modeClass(mode === 'board')}>
              Board
            </button>
          </span>
          <button
            type="button"
            onClick={openCreate}
            className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
          >
            + Add task
          </button>
        </span>
      }
    >
      <div className="hidden md:block">
        <TaskViews active={view} search={search} onSelectView={selectView} onSearchChange={setSearch} />
      </div>
      <div className="flex justify-end md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="cap-label cursor-pointer"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Views & search">
        <TaskViews
          active={view}
          search={search}
          onSelectView={(next) => {
            selectView(next);
            setSheetOpen(false);
          }}
          onSearchChange={setSearch}
        />
      </LedgerSheet>

      <div className={cn('mt-1', peekOpen && peekTask && isDesktop && 'lg:grid lg:grid-cols-[1fr_340px]')}>
        <div className="min-w-0">
          {mode === 'board' ? (
            <>
              <div className="hidden md:block">
                <TaskBoard
                  columns={visibleColumns}
                  selectedId={selectedId}
                  busy={busy}
                  onSelect={(id) => {
                    setSelectedId(id);
                    setPeekOpen(true);
                  }}
                  onDropStatus={dropStatus}
                />
              </div>
              <div className="md:hidden">
                <TaskGroups
                  groups={listGroups}
                  selectedId={selectedId}
                  busy={busy}
                  onSelect={(id) => {
                    setSelectedId(id);
                    setPeekOpen(true);
                  }}
                  onToggleComplete={toggleComplete}
                  onClaim={claim}
                  onOpenRisk={openRisk}
                  onResetView={resetView}
                />
              </div>
            </>
          ) : (
            <TaskGroups
              groups={listGroups}
              selectedId={selectedId}
              busy={busy}
              onSelect={(id) => {
                setSelectedId(id);
                setPeekOpen(true);
              }}
              onToggleComplete={toggleComplete}
              onClaim={claim}
              onOpenRisk={openRisk}
              onResetView={resetView}
            />
          )}
        </div>
        {peekOpen && peekTask && isDesktop && (
          <TaskPeek task={peekTask} onClose={() => setPeekOpen(false)} onEdit={openEdit} onOpenRisk={openRisk} />
        )}
      </div>

      <LedgerSheet
        open={peekOpen && !isDesktop && peekTask != null}
        onOpenChange={(open) => {
          if (!open) setPeekOpen(false);
        }}
        title="Task"
      >
        {peekTask && (
          <TaskPeek task={peekTask} onClose={() => setPeekOpen(false)} onEdit={openEdit} onOpenRisk={openRisk} inSheet />
        )}
      </LedgerSheet>

      <TaskFormDialog open={dialogOpen} task={editingTask} onClose={closeDialog} onSuccess={handleFormSuccess} />
    </PageWrapper>
  );
};
